/**
 * Insurance Firestore normalization
 *
 * Maps Airtable-synced `insurances` documents to canonical `Insurance` types and
 * reads billing fields under legacy or canonical property names.
 */

import type { Insurance } from '@physiofit-digital/physiofit_types';

type BillingPatientFirestore = {
  type?: string;
  billingPatientType?: string;
  blockedReason?: string;
  billingPatientBlockReason?: string;
};

type InsuranceBonusFirestore = NonNullable<Insurance['bonus']> & {
  bonusDescription?: string;
  bonusInfoUrl?: string;
};

type BonusFirestore = InsuranceBonusFirestore;

const BILLING_TYPES = new Set([
  'blocked',
  'invoice',
  'abtretung',
  'without_abtretung_physiofit',
  'without_abtretung_patient',
  'bnpl',
]);

function normalizeBillingPatient(raw: BillingPatientFirestore | undefined): Insurance['billingPatient'] | undefined {
  if (!raw) {
    return undefined;
  }
  const typeRaw = (raw.type ?? raw.billingPatientType)?.trim();
  if (!typeRaw || !BILLING_TYPES.has(typeRaw)) {
    return undefined;
  }
  const type = typeRaw as NonNullable<Insurance['billingPatient']>['type'];
  const blockedReasonRaw = (raw.blockedReason ?? raw.billingPatientBlockReason)?.trim();
  const blockedReason = blockedReasonRaw as NonNullable<Insurance['billingPatient']>['blockedReason'] | undefined;
  return {
    type,
    ...(blockedReason ? { blockedReason } : {}),
  };
}

function normalizeBonus(raw: BonusFirestore | undefined): Insurance['bonus'] {
  if (!raw) {
    return { hasBonus: false };
  }
  const description = (raw.description ?? raw.bonusDescription)?.trim();
  const infoUrl = raw.infoUrl ?? raw.bonusInfoUrl;
  const valueEuro =
    typeof raw.valueEuro === 'number' && Number.isFinite(raw.valueEuro) && raw.valueEuro > 0
      ? raw.valueEuro
      : undefined;
  const valueName = raw.valueName?.trim();
  return {
    hasBonus: raw.hasBonus ?? false,
    ...(valueEuro ? { valueEuro } : {}),
    ...(valueName ? { valueName } : {}),
    ...(infoUrl ? { infoUrl } : {}),
    ...(description ? { description } : {}),
  };
}

/**
 * Maps a Firestore `insurances` document to the canonical `Insurance` shape.
 *
 * @param {string} docId Firestore document id.
 * @param {Object | undefined} data Raw document fields.
 * @param {Object | undefined} options Optional fallback name and hidden-row allowance.
 * @return {Object | null} Normalized insurance or null when hidden or invalid.
 */
export function normalizeInsuranceFromFirestore(
  docId: string,
  data: Record<string, unknown> | undefined,
  options?: { fallbackName?: string; allowHidden?: boolean },
): Insurance | null {
  if (!data) {
    return null;
  }
  if (data.hideInsurance === true && !options?.allowHidden) {
    return null;
  }

  const partial = data as Partial<Insurance> & {
    billingPatient?: BillingPatientFirestore;
    bonus?: BonusFirestore;
  };

  const id = typeof partial.id === 'string' && partial.id.length > 0 ? partial.id : docId;
  const name =
    typeof partial.name === 'string' && partial.name.length > 0 ? partial.name : (options?.fallbackName ?? '');

  return {
    ...(partial as Insurance),
    id,
    name,
    isFavorite: partial.isFavorite ?? false,
    privateInsurance: partial.privateInsurance ?? false,
    submitType: partial.submitType ?? 'no_submit',
    hideInsurance: partial.hideInsurance ?? false,
    bonus: normalizeBonus(partial.bonus),
    billingPatient: normalizeBillingPatient(partial.billingPatient),
  };
}

/**
 * Reads billing type from either canonical or legacy Firestore field names.
 *
 * @param {Object} insurance Normalized insurance row.
 * @return {string | undefined} Billing patient type when valid.
 */
export function readBillingPatientType(
  insurance: Insurance,
): NonNullable<Insurance['billingPatient']>['type'] | undefined {
  const bp = insurance.billingPatient as BillingPatientFirestore | undefined;
  const raw = (bp?.type ?? bp?.billingPatientType)?.trim();
  if (raw && BILLING_TYPES.has(raw)) {
    return raw as NonNullable<Insurance['billingPatient']>['type'];
  }
  return undefined;
}

/**
 * Reads blocked reason from canonical or legacy field names on the insurance row.
 *
 * @param {Object} insurance Normalized insurance row.
 * @return {string | undefined} Blocked reason code when set.
 */
export function readBillingPatientBlockedReason(insurance: Insurance): string | undefined {
  const bp = insurance.billingPatient as BillingPatientFirestore | undefined;
  return (bp?.blockedReason ?? bp?.billingPatientBlockReason)?.trim() || undefined;
}
